import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
var _excluded = ["currentTs", "className", "visible", "onOk", "onChange", "onValueChange", "mode", "typeArr", "minTs", "maxTs", "formatter", "valueFilter"];
import React, { useEffect, useRef, useState, useMemo, forwardRef, useImperativeHandle } from 'react';
import { cls } from '@arco-design/mobile-utils';
import Picker from '../picker';
import { ContextLayout } from '../context-provider';
import { convertTsToDateObj, oneOf, judgeObj, convertObjToTs } from './helper';
export * from './type';
var allTypes = ['year', 'month', 'date', 'hour', 'minute', 'second'];

var defaultFormatter = function defaultFormatter(value) {
  return value < 10 ? "0" + value : String(value);
};

export var YEAR = 12 * 30 * 24 * 60 * 60 * 1000;
var initMinDate = Date.now() - 10 * YEAR;
var initMaxDate = Date.now() + 10 * YEAR;
var initDate = Date.now();

/**
 * 日期时间选择器，基于`Picker`组件扩展，支持指定范围，单位可精确到秒。
 * @type 数据录入
 * @name 日期时间选择器
 */
var DatePicker = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$currentTs = props.currentTs,
      userSetCurrentTs = _props$currentTs === void 0 ? initDate : _props$currentTs,
      _props$className = props.className,
      className = _props$className === void 0 ? '' : _props$className,
      _props$visible = props.visible,
      visible = _props$visible === void 0 ? false : _props$visible,
      onOk = props.onOk,
      onChange = props.onChange,
      onValueChange = props.onValueChange,
      _props$mode = props.mode,
      mode = _props$mode === void 0 ? 'datetime' : _props$mode,
      _props$typeArr = props.typeArr,
      typeArr = _props$typeArr === void 0 ? [] : _props$typeArr,
      _props$minTs = props.minTs,
      minTs = _props$minTs === void 0 ? initMinDate : _props$minTs,
      _props$maxTs = props.maxTs,
      maxTs = _props$maxTs === void 0 ? initMaxDate : _props$maxTs,
      _props$formatter = props.formatter,
      formatter = _props$formatter === void 0 ? defaultFormatter : _props$formatter,
      _props$valueFilter = props.valueFilter,
      valueFilter = _props$valueFilter === void 0 ? function () {
    return true;
  } : _props$valueFilter,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded);

  var currentTs = Math.min(maxTs, Math.max(minTs, userSetCurrentTs));

  var _useState = useState([[]]),
      data = _useState[0],
      setData = _useState[1];

  var _useState2 = useState([]),
      value = _useState2[0],
      setValue = _useState2[1];

  var currentDateObjRef = useRef(convertTsToDateObj(currentTs));
  var minDateObjRef = useRef(convertTsToDateObj(minTs));
  var maxDateObjRef = useRef(convertTsToDateObj(maxTs));
  var keyOptions = useMemo(function () {
    return _getKeyOptions();
  }, [mode, typeArr]);
  var pickerRef = useRef(null);
  useImperativeHandle(ref, function () {
    return {
      dom: pickerRef.current ? pickerRef.current.dom : null
    };
  });

  function _getColumns() {
    var dateObj = {};
    allTypes.forEach(function (type) {
      dateObj[type] = _getActualArray(type, currentDateObjRef.current);
    });
    var columns = keyOptions.map(function (opt) {
      return dateObj[opt];
    });
    return {
      columns: columns,
      dateObj: dateObj
    };
  }

  function _getSelectValue(columns) {
    var val = keyOptions.map(function (opt, index) {
      var selectIndex = columns[index].findIndex(function (col) {
        return col.value === currentDateObjRef.current[opt];
      });
      return columns[index][Math.max(selectIndex, 0)].value;
    });
    return val;
  }

  function _initData() {
    var _getColumns2 = _getColumns(),
        columns = _getColumns2.columns;

    var val = _getSelectValue(columns);

    setData(function () {
      return columns;
    });
    setValue(function () {
      return val;
    });
  } // 根据当前选中的日期动态改变其他列的options


  function _getActualArray(type, nowDateObj) {
    var normalRange = _getNormalRange(type, nowDateObj);

    var range = [];

    switch (type) {
      case 'year':
        range = [minDateObjRef.current.year, maxDateObjRef.current.year];
        break;

      default:
        {
          var checkKeys = allTypes.slice(0, allTypes.indexOf(type));
          range = normalRange;

          if (judgeObj(nowDateObj, minDateObjRef.current, checkKeys)) {
            range[0] = minDateObjRef.current[type];
          }

          if (judgeObj(nowDateObj, maxDateObjRef.current, checkKeys)) {
            range[range.length - 1] = maxDateObjRef.current[type];
          }
        }
    }

    return _convertRangeToArr(type, range);
  }

  function _convertRangeToArr(type, range) {
    var _range$ = range[0],
        start = _range$ === void 0 ? 0 : _range$,
        _range$2 = range[1],
        end = _range$2 === void 0 ? 0 : _range$2;
    var arr = [];

    for (var i = start; i <= end; i += 1) {
      if (valueFilter(type, i)) {
        var text = formatter(i, type);
        arr.push({
          label: text === void 0 ? defaultFormatter(i) : text,
          value: i
        });
      }
    }

    return arr;
  }

  function _getNormalRange(type, nowDateObj) {
    switch (type) {
      case 'month':
        return [1, 12];

      case 'date':
        if (nowDateObj.month === 2) {
          // 闰年2月29天
          return nowDateObj.year % 4 === 0 ? [1, 29] : [1, 28];
        }

        return oneOf(nowDateObj.month, [1, 3, 5, 7, 8, 10, 12]) ? [1, 31] : [1, 30];

      case 'hour':
        return [0, 23];

      case 'minute':
      case 'second':
        return [0, 59];

      default:
        return [];
    }
  }

  function _handlePickerChange(values, index) {
    var type = keyOptions[index];
    var nowDateObj = {};
    values.forEach(function (i, keyIndex) {
      nowDateObj[keyOptions[keyIndex]] = i;
    });

    if (type === 'month' || type === 'year') {
      currentDateObjRef.current = nowDateObj;

      var _getColumns3 = _getColumns(),
          columns = _getColumns3.columns,
          dateObj = _getColumns3.dateObj; // 校准选中日期
      // 比如先选中2020-02-29 年份改为2019，则2月没有29日；需要校准


      keyOptions.forEach(function (key) {
        if (dateObj[key].findIndex(function (item) {
          return item.value === nowDateObj[key];
        }) < 0) {
          nowDateObj[key] = dateObj[key][dateObj[key].length - 1].value;
        }
      });

      var val = _getSelectValue(columns);

      setData(columns);
      setValue(val);
    }

    if (onValueChange) {
      onValueChange(convertObjToTs(nowDateObj, currentTs), nowDateObj, index);
    }
  }

  function _handlePickerConfirm(values) {
    var nowDateObj = {};
    values.forEach(function (index, keyIndex) {
      nowDateObj[keyOptions[keyIndex]] = index;
    });
    var newTs = convertObjToTs(nowDateObj, currentTs);

    if (onOk) {
      onOk(newTs, nowDateObj);
    }

    if (onChange) {
      onChange(newTs, nowDateObj);
    }
  }

  function _getKeyOptions() {
    if (typeArr && typeArr.length) {
      return typeArr;
    }

    var options = [];

    switch (mode) {
      case 'date':
        options = allTypes.slice(0, 3);
        break;

      case 'time':
        options = allTypes.slice(3);
        break;

      default:
        options = allTypes;
    }

    return options;
  }

  useEffect(function () {
    minDateObjRef.current = convertTsToDateObj(minTs);
    currentDateObjRef.current = convertTsToDateObj(currentTs);
    maxDateObjRef.current = convertTsToDateObj(maxTs);

    _initData();
  }, [currentTs, minTs, maxTs]);
  useEffect(function () {
    if (visible) {
      currentDateObjRef.current = convertTsToDateObj(currentTs);

      _initData();
    }
  }, [visible]);
  return /*#__PURE__*/React.createElement(ContextLayout, null, function (_ref) {
    var prefixCls = _ref.prefixCls;
    return /*#__PURE__*/React.createElement(Picker, _extends({}, otherProps, {
      ref: pickerRef,
      visible: visible,
      className: cls(className, prefixCls + "-date-picker"),
      cascade: false,
      data: data,
      value: value,
      onPickerChange: _handlePickerChange,
      onOk: _handlePickerConfirm
    }));
  });
});
export default DatePicker;